import { useState, useEffect } from 'react'
import { Sparkles, Search, FileText, Check, ArrowRight, Loader2, BrainCircuit } from 'lucide-react'

const WORKFLOW_STEPS = {
    IDLE: 'idle',
    RESEARCHING: 'researching',
    STRUCTURING: 'structuring',
    DRAFTING: 'drafting',
    COMPLETED: 'completed'
}

export default function AgentWorkflow() {
    const [status, setStatus] = useState(WORKFLOW_STEPS.IDLE)
    const [topic, setTopic] = useState('')
    const [logs, setLogs] = useState([])
    const [result, setResult] = useState(null)

    const addLog = (message) => {
        setLogs(prev => [...prev, { message, timestamp: new Date(), id: Math.random() }])
    }

    const startWorkflow = () => {
        if (!topic.trim()) return
        setStatus(WORKFLOW_STEPS.RESEARCHING)
        setLogs([])
        setResult(null)
    }

    // Mock Workflow Execution
    useEffect(() => {
        if (status === WORKFLOW_STEPS.RESEARCHING) {
            addLog(`Analyzing topic: "${topic}"...`)
            setTimeout(() => {
                addLog('Searching for relevant data sources...')
            }, 1000)
            setTimeout(() => {
                addLog('Extracting key insights and competitors...')
            }, 2500)
            setTimeout(() => {
                addLog('Research phase complete.')
                setStatus(WORKFLOW_STEPS.STRUCTURING)
            }, 4000)
        }

        if (status === WORKFLOW_STEPS.STRUCTURING) {
            setTimeout(() => {
                addLog('Organizing research into logical flow...')
            }, 1000)
            setTimeout(() => {
                addLog('Defining SEO meta-tags and heading structure...')
            }, 2000)
            setTimeout(() => {
                addLog('Structure defined. Preparing to draft.')
                setStatus(WORKFLOW_STEPS.DRAFTING)
            }, 3500)
        }

        if (status === WORKFLOW_STEPS.DRAFTING) {
            setTimeout(() => {
                addLog('Drafting introduction with hook...')
            }, 1000)
            setTimeout(() => {
                addLog('Expanding main body sections...')
            }, 3000)
            setTimeout(() => {
                addLog('Polishing conclusion and call-to-action...')
            }, 5000)
            setTimeout(() => {
                setStatus(WORKFLOW_STEPS.COMPLETED)
                setResult({
                    title: `The Ultimate Guide to ${topic}`,
                    content: `Here is a comprehensive draft covering "${topic}". This content was generated by analyzing top competitors, extracting key insights, and structuring them for maximum readability and SEO impact.\n\n(This is a placeholder for the actual generated content.)`
                })
            }, 6000)
        }
    }, [status, topic])

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            {/* Input Section */}
            <div className={`transition-all duration-500 ${status !== WORKFLOW_STEPS.IDLE ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 sm:p-10 backdrop-blur-sm">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
                            <BrainCircuit className="w-8 h-8 text-white" />
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                            What should I build for you?
                        </h2>
                        <p className="text-slate-400 max-w-lg mx-auto text-lg">
                            I don't just write. I research, structure, and draft entire documents in one mission.
                        </p>
                    </div>

                    <div className="relative max-w-2xl mx-auto">
                        <input
                            type="text"
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && startWorkflow()}
                            placeholder="e.g., A technical guide on implementing OAuth 2.0..."
                            className="w-full bg-slate-800/50 text-white placeholder-slate-500 border border-white/10 rounded-xl px-6 py-4 pr-32 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all text-lg shadow-inner"
                        />
                        <button
                            onClick={startWorkflow}
                            disabled={!topic.trim()}
                            className="absolute right-2 top-2 bottom-2 px-6 bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-400 hover:to-blue-500 text-white font-medium rounded-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                        >
                            <Sparkles className="w-4 h-4" />
                            Start
                        </button>
                    </div>
                </div>
            </div>

            {/* Workflow Visualization */}
            {status !== WORKFLOW_STEPS.IDLE && (
                <div className="space-y-6 animate-fade-in-up">
                    {/* Status Steps */}
                    <div className="flex items-center justify-between max-w-2xl mx-auto px-4">
                        <StepIndicator
                            currentStatus={status}
                            step={WORKFLOW_STEPS.RESEARCHING}
                            icon={Search}
                            label="Research"
                        />
                        <div className="flex-1 h-0.5 mx-4 bg-white/10 relative">
                            <div
                                className={`absolute inset-0 bg-blue-500 transition-all duration-1000 ${[WORKFLOW_STEPS.STRUCTURING, WORKFLOW_STEPS.DRAFTING, WORKFLOW_STEPS.COMPLETED].includes(status) ? 'w-full' : 'w-0'
                                    }`}
                            />
                        </div>
                        <StepIndicator
                            currentStatus={status}
                            step={WORKFLOW_STEPS.STRUCTURING}
                            icon={FileText}
                            label="Structure"
                        />
                        <div className="flex-1 h-0.5 mx-4 bg-white/10 relative">
                            <div
                                className={`absolute inset-0 bg-blue-500 transition-all duration-1000 ${[WORKFLOW_STEPS.DRAFTING, WORKFLOW_STEPS.COMPLETED].includes(status) ? 'w-full' : 'w-0'
                                    }`}
                            />
                        </div>
                        <StepIndicator
                            currentStatus={status}
                            step={WORKFLOW_STEPS.DRAFTING}
                            icon={Sparkles}
                            label="Drafting"
                        />
                    </div>

                    {/* Terminal / Logs Area */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-black/80 rounded-xl p-6 font-mono text-sm border border-white/10 h-[300px] overflow-y-auto custom-scrollbar shadow-2xl">
                            <div className="flex items-center gap-2 text-slate-500 mb-4 pb-2 border-b border-white/10">
                                <span className="w-3 h-3 rounded-full bg-red-500/50" />
                                <span className="w-3 h-3 rounded-full bg-yellow-500/50" />
                                <span className="w-3 h-3 rounded-full bg-green-500/50" />
                                <span className="ml-auto text-xs">AGENT_LOGS</span>
                            </div>
                            <div className="space-y-2">
                                {logs.map((log) => (
                                    <div key={log.id} className="flex gap-2 text-green-400/90 animate-fade-in">
                                        <span className="opacity-50">[{log.timestamp.toLocaleTimeString().split(' ')[0]}]</span>
                                        <span>{'>'} {log.message}</span>
                                    </div>
                                ))}
                                {status !== WORKFLOW_STEPS.COMPLETED && (
                                    <div className="flex gap-2 text-green-400/50 animate-pulse">
                                        <span className="opacity-0">[]</span>
                                        <span>_</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Result Preview Area */}
                        <div className="bg-slate-900 rounded-xl p-6 border border-white/10 h-[300px] flex flex-col relative overflow-hidden">
                            {result ? (
                                <div className="space-y-4 animate-fade-in h-full overflow-y-auto custom-scrollbar">
                                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                        <Check className="w-5 h-5 text-green-400" />
                                        Mission Complete
                                    </h3>
                                    <div className="bg-white/5 rounded-lg p-4 h-full border border-white/5">
                                        <h4 className="font-semibold text-blue-300 mb-2">{result.title}</h4>
                                        <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{result.content}</p>
                                    </div>
                                    <div className="mt-auto pt-4 flex gap-2 justify-end">
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(result.content)
                                                alert('Copied to clipboard!')
                                            }}
                                            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm"
                                        >
                                            Copy to Clipboard
                                        </button>
                                        <button
                                            onClick={() => window.location.href = '/app'}
                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm flex items-center gap-2"
                                        >
                                            Open in Editor <ArrowRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 gap-4">
                                    <div className="relative">
                                        <div className="w-16 h-16 border-4 border-slate-700 border-t-blue-500 rounded-full animate-spin" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <BrainCircuit className="w-6 h-6 text-slate-700" />
                                        </div>
                                    </div>
                                    <p className="animate-pulse">Agent is working...</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

function StepIndicator({ currentStatus, step, icon: Icon, label }) {
    const isCompleted = getStepOrder(currentStatus) > getStepOrder(step)
    const isActive = currentStatus === step

    return (
        <div className={`flex flex-col items-center gap-2 relative z-10 ${isActive ? 'scale-110' : ''} transition-transform`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500
                ${isCompleted || isActive ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/30' : 'bg-slate-900 border-slate-700 text-slate-500'}
            `}>
                {isCompleted ? <Check className="w-5 h-5" /> : <Icon className={`w-5 h-5 ${isActive ? 'animate-pulse' : ''}`} />}
            </div>
            <span className={`text-xs font-medium transition-colors duration-300 ${isCompleted || isActive ? 'text-blue-400' : 'text-slate-600'}`}>
                {label}
            </span>
        </div>
    )
}

function getStepOrder(step) {
    const steps = ['idle', 'researching', 'structuring', 'drafting', 'completed']
    return steps.indexOf(step)
}
